import {
  ArrowUpRight,
  ArrowRight,
  Boxes,
  Workflow,
  GitBranch,
  Webhook,
  Database,
  ShieldCheck,
  Zap,
  Activity,
  Layers,
  Cpu,
  BarChart3,
  Bot,
  PhoneCall,
  Target,
  Sparkles,
  Search,
  CheckCircle2,
  Share2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SectionCta } from "./service-revops-section-cta";
import { gohighlevelConfig as defaultConfig } from "./service-platform-config";
import type { PlatformConfig } from "./service-platform-config";

// Topic & Category icon mapping for SVG graphic display in architecture cards
function getCardIcon(tag: string, label: string): LucideIcon {
  const t = (tag + " " + label).toLowerCase();
  if (t.includes("route") || t.includes("branch") || t.includes("decision"))
    return GitBranch;
  if (t.includes("webhook") || t.includes("event") || t.includes("trigger"))
    return Webhook;
  if (
    t.includes("sync") ||
    t.includes("data") ||
    t.includes("crm") ||
    t.includes("database")
  )
    return Database;
  if (
    t.includes("error") ||
    t.includes("retry") ||
    t.includes("heal") ||
    t.includes("reliability")
  )
    return ShieldCheck;
  if (
    t.includes("call") ||
    t.includes("voice") ||
    t.includes("dialer") ||
    t.includes("vapi")
  )
    return PhoneCall;
  if (t.includes("ai") || t.includes("agent") || t.includes("bot")) return Bot;
  if (
    t.includes("track") ||
    t.includes("pixel") ||
    t.includes("conversion") ||
    t.includes("capi")
  )
    return Target;
  if (
    t.includes("analytic") ||
    t.includes("dashboard") ||
    t.includes("report") ||
    t.includes("revenue")
  )
    return BarChart3;
  if (t.includes("keyword") || t.includes("search") || t.includes("bidding"))
    return Search;
  if (
    t.includes("onboard") ||
    t.includes("provision") ||
    t.includes("snapshot")
  )
    return Layers;
  if (t.includes("pipeline") || t.includes("cadence") || t.includes("sequence"))
    return Workflow;
  return Zap;
}

export function ServiceArchitectureSection({
  config = defaultConfig,
}: {
  config?: PlatformConfig;
} = {}) {
  const proofShots = config.archProofShots;
  const capabilities = config.capabilities;
  return (
    <section className="relative overflow-hidden py-20 md:py-28 px-5 sm:px-8 lg:px-12 revops-builds-showcase-section">
      {/* Layer 0: Base white-to-pale-blue subtle gradient */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "linear-gradient(180deg, #FFFFFF 0%, #F9FCFF 32%, #F2F8FF 68%, #EAF5FF 100%)",
        }}
      />

      {/* Layer 1: Soft blue radial atmosphere matching the reference exactly */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          backgroundImage: `
            radial-gradient(ellipse 75% 45% at 50% 28%, rgba(56, 182, 255, 0.09) 0%, rgba(56, 182, 255, 0.03) 48%, transparent 72%),
            radial-gradient(circle at 10% 45%, rgba(47, 165, 255, 0.08) 0%, transparent 55%),
            radial-gradient(circle at 90% 70%, rgba(11, 124, 242, 0.07) 0%, transparent 55%)
          `,
        }}
      />

      {/* Layer 2: Abstract pale architectural curves entering from edges */}
      {/* Upper-Left curve */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute -top-12 -left-16 w-[460px] sm:w-[620px] lg:w-[800px] h-auto z-[2] select-none opacity-85"
        viewBox="0 0 800 540"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 0C220 80 400 190 450 330C500 470 410 525 360 540H0V0Z"
          fill="url(#s3-wave-ul)"
        />
        <path
          d="M0 0C220 80 400 190 450 330C500 470 410 525 360 540"
          stroke="rgba(11, 124, 242, 0.12)"
          strokeWidth="1.5"
          fill="none"
        />
        <defs>
          <linearGradient
            id="s3-wave-ul"
            x1="0"
            y1="0"
            x2="460"
            y2="540"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#EAF7FF" stopOpacity="0.75" />
            <stop offset="0.7" stopColor="#DDF2FF" stopOpacity="0.3" />
            <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* Upper-Right curve */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute top-6 -right-20 w-[440px] sm:w-[580px] lg:w-[740px] h-auto z-[2] select-none opacity-80"
        viewBox="0 0 740 500"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M740 0C570 90 400 200 370 340C340 480 420 490 460 500H740V0Z"
          fill="url(#s3-wave-ur)"
        />
        <path
          d="M740 0C570 90 400 200 370 340C340 480 420 490 460 500"
          stroke="rgba(11, 124, 242, 0.1)"
          strokeWidth="1.5"
          fill="none"
        />
        <defs>
          <linearGradient
            id="s3-wave-ur"
            x1="740"
            y1="0"
            x2="370"
            y2="500"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#EAF7FF" stopOpacity="0.7" />
            <stop offset="0.65" stopColor="#DDF2FF" stopOpacity="0.25" />
            <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* Lower-Left broad curve */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-16 -left-12 w-[500px] sm:w-[700px] lg:w-[880px] h-auto z-[2] select-none opacity-85"
        viewBox="0 0 880 440"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 440C210 380 440 310 650 330C780 344 850 400 880 440H0Z"
          fill="url(#s3-wave-ll)"
        />
        <path
          d="M0 395C250 320 480 270 700 300C790 315 850 350 880 380"
          stroke="rgba(11, 124, 242, 0.12)"
          strokeWidth="1.5"
          fill="none"
        />
        <defs>
          <linearGradient
            id="s3-wave-ll"
            x1="0"
            y1="310"
            x2="880"
            y2="440"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#DDF2FF" stopOpacity="0.7" />
            <stop offset="0.65" stopColor="#EAF7FF" stopOpacity="0.25" />
            <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* Lower-Right sweep */}
      <svg
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-10 -right-12 w-[400px] sm:w-[540px] lg:w-[660px] h-auto z-[2] select-none opacity-75"
        viewBox="0 0 660 380"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M660 380C510 320 360 275 190 295C100 307 30 345 0 380H660Z"
          fill="url(#s3-wave-lr)"
        />
        <path
          d="M660 340C490 270 320 250 160 278C80 295 20 325 0 350"
          stroke="rgba(11, 124, 242, 0.1)"
          strokeWidth="1.2"
          fill="none"
        />
        <defs>
          <linearGradient
            id="s3-wave-lr"
            x1="660"
            y1="275"
            x2="0"
            y2="380"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#CFECFF" stopOpacity="0.5" />
            <stop offset="0.7" stopColor="#EAF7FF" stopOpacity="0.18" />
            <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* Main Section Content Container */}
      <div className="relative z-[3] mx-auto max-w-[1480px]">
        {/* Main Top Header */}
        <div className="text-center mb-12 md:mb-16">
          <div
            className="inline-flex items-center gap-2 mb-3.5 font-sans font-bold uppercase tracking-[0.22em]"
            style={{
              fontSize: "11px",
              color: "#0B7CF2",
            }}
          >
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: "#27DC5D" }}
            />
            {config.archEyebrow}
          </div>

          <h2
            className="font-display font-extrabold"
            style={{
              fontSize: "clamp(46px, 5.6vw, 84px)",
              lineHeight: 0.94,
              letterSpacing: "-0.05em",
              color: "#071E61",
            }}
          >
            <span className="block">{config.archHeadlineNavy}</span>
            <span className="block" style={{ color: "#0B7CF2" }}>
              {config.archHeadlineBlue}
            </span>
          </h2>

          <p
            className="mx-auto mt-5 max-w-[820px] text-balance"
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "18px",
              lineHeight: 1.55,
              color: "#536B89",
            }}
          >
            <strong style={{ fontWeight: 650, color: "#082E67" }}>
              {config.archSubEmphasis}
            </strong>{" "}
            {config.archSub}
          </p>
        </div>

        {/* Section Gallery Wrapper with Giant Pale ARCHITECTURE Watermark behind */}
        <div className="relative my-6 md:my-10">
          {/* Giant Background Typography */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none font-display font-black tracking-[-0.07em] text-center whitespace-nowrap z-0"
            style={{
              fontSize: "clamp(120px, 17vw, 320px)",
              lineHeight: 0.78,
              color: "rgba(17,143,247,0.06)",
              width: "100%",
            }}
          >
            {config.architectureGhostWord}
          </div>

          {/* Sub-label: ● SELECTED ARCHITECTURE BUILDS */}
          <div
            className="relative z-10 flex items-center justify-center gap-2 mb-6 font-sans font-bold uppercase tracking-[0.14em]"
            style={{
              fontSize: "11px",
              color: "#0B7CF2",
            }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "#27DC5D" }}
            />
            SELECTED ARCHITECTURE BUILDS
          </div>

          {/* 4 Architecture Showcase Cards */}
          <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 overflow-x-auto lg:overflow-visible pb-2 sm:pb-0 snap-x">
            {proofShots.map((s) => (
              <div
                key={s.label}
                className="revops-arch-shot-card group relative flex flex-col rounded-[24px] overflow-hidden transition-all duration-300 snap-start"
                style={{
                  background: "#FFFFFF",
                  border: "1px solid rgba(8,46,103,0.08)",
                  boxShadow:
                    "0 14px 38px rgba(8,46,103,0.07), 0 2px 6px rgba(8,46,103,0.025)",
                }}
              >
                {/* Browser Chrome Header */}
                <div
                  className="flex items-center justify-between px-4 py-2.5"
                  style={{
                    background: "#F8FCFF",
                    borderBottom: "1px solid rgba(8,46,103,0.06)",
                  }}
                >
                  {/* 3-dot window buttons */}
                  <div className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F56] inline-block" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E] inline-block" />
                    <span className="w-2.5 h-2.5 rounded-full bg-[#27C93F] inline-block" />
                  </div>

                  {/* LIVE SYSTEM Indicator Badge */}
                  <div
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-sans font-bold"
                    style={{
                      fontSize: "9.5px",
                      letterSpacing: "0.06em",
                      color: "#169846",
                    }}
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: "#27DC5D" }}
                    />
                    LIVE SYSTEM
                  </div>
                </div>

                {/* SVG Graphic Blueprint Area (Replacing screenshot) */}
                <div className="relative aspect-[16/10.5] bg-gradient-to-br from-[#F5FAFF] via-[#EDF6FF] to-[#E3F2FD] overflow-hidden border-b border-[rgba(8,46,103,0.06)] flex items-center justify-center p-6 group-hover:from-[#EEF7FF] group-hover:to-[#D9ECFF] transition-colors duration-300">
                  {/* Subtle architectural grid pattern */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-[0.35]"
                    style={{
                      backgroundImage: `
                        linear-gradient(to right, rgba(11, 124, 242, 0.08) 1px, transparent 1px),
                        linear-gradient(to bottom, rgba(11, 124, 242, 0.08) 1px, transparent 1px)
                      `,
                      backgroundSize: "20px 20px",
                    }}
                  />

                  {/* Flow connection lines SVG */}
                  <svg
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 w-full h-full opacity-30"
                    viewBox="0 0 280 180"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 90 H 80 Q 110 90 110 60 V 40 Q 110 20 140 20 H 260"
                      stroke="#0B7CF2"
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                    <path
                      d="M20 90 H 80 Q 110 90 110 120 V 140 Q 110 160 140 160 H 260"
                      stroke="#0B7CF2"
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                    <circle cx="80" cy="90" r="3" fill="#0B7CF2" />
                    <circle cx="200" cy="20" r="3" fill="#27DC5D" />
                    <circle cx="200" cy="160" r="3" fill="#27DC5D" />
                  </svg>

                  {/* Central SVG Graphic Icon Badge */}
                  {(() => {
                    const CardIcon = getCardIcon(s.tag, s.label);
                    return (
                      <div className="relative z-10 flex flex-col items-center justify-center">
                        <div
                          className="w-16 h-16 sm:w-18 sm:h-18 rounded-2xl flex items-center justify-center shadow-[0_10px_26px_rgba(11,124,242,0.18)] transform group-hover:scale-105 group-hover:-translate-y-0.5 transition-all duration-300"
                          style={{
                            background:
                              "linear-gradient(135deg, #0B7CF2 0%, #0860BE 100%)",
                            border: "1.5px solid rgba(255,255,255,0.8)",
                          }}
                        >
                          <CardIcon className="w-8 h-8 sm:w-9 sm:h-9 text-white stroke-[1.8]" />
                        </div>
                        <div className="mt-2.5 px-3 py-1 rounded-full bg-white/85 backdrop-blur-xs border border-[#0B7CF2]/15 text-[10px] font-mono font-bold tracking-wider uppercase text-[#0B7CF2] shadow-xs">
                          {s.tag.split("•")[1]?.trim() || "SYSTEM"}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Card Body Content */}
                <div className="p-5 sm:p-5.5 flex flex-col flex-1 justify-between bg-white">
                  <div>
                    {/* Editorial Tag: 0X • CATEGORY */}
                    <div
                      className="font-sans font-bold uppercase tracking-[0.08em] mb-2"
                      style={{
                        fontSize: "10px",
                        color: "#0B7CF2",
                      }}
                    >
                      {s.tag}
                    </div>

                    {/* Title */}
                    <h3
                      className="font-display font-extrabold mb-1.5"
                      style={{
                        fontSize: "19px",
                        lineHeight: 1.15,
                        letterSpacing: "-0.025em",
                        color: "#071E61",
                      }}
                    >
                      {s.label}
                    </h3>

                    {/* Description */}
                    <p
                      className="line-clamp-2"
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: "13.5px",
                        lineHeight: 1.45,
                        color: "#536B89",
                      }}
                    >
                      {s.desc}
                    </p>
                  </div>

                  {/* Bottom CTA Row: "View architecture →" + Circle Arrow */}
                  <div className="mt-5 pt-3.5 flex items-center justify-between border-t border-[rgba(8,46,103,0.06)]">
                    <span
                      className="revops-arch-cta-btn inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-sans font-bold transition-colors duration-200"
                      style={{
                        fontSize: "12px",
                        color: "#0B7CF2",
                        background: "#EDF7FF",
                      }}
                    >
                      View architecture <ArrowRight className="w-3.5 h-3.5" />
                    </span>

                    <div
                      aria-hidden="true"
                      className="revops-arch-cta-arrow w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-200"
                      style={{
                        background: "#EDF7FF",
                        color: "#0B7CF2",
                      }}
                    >
                      <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Transition Divider: ● THE ARCHITECTURE UNDERNEATH */}
        <div className="mt-18 md:mt-24 mb-10 md:mb-12 text-center">
          <div
            className="inline-flex items-center gap-2 mb-3 font-sans font-bold uppercase tracking-[0.16em]"
            style={{
              fontSize: "11px",
              color: "#0B7CF2",
            }}
          >
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: "#27DC5D" }}
            />
            {config.archUnderneathLabel}
          </div>

          <h3
            className="font-display font-extrabold"
            style={{
              fontSize: "clamp(34px, 4vw, 56px)",
              lineHeight: 1.02,
              letterSpacing: "-0.04em",
              color: "#071E61",
            }}
          >
            <span>{config.archUnderHeadlineNavy} </span>
            <span style={{ color: "#0B7CF2" }}>
              {config.archUnderHeadlineBlue}
            </span>
          </h3>
        </div>

        {/* 3x2 Capability Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {capabilities.map((c, i) => {
            const numStr = String(i + 1).padStart(2, "0");
            const isCardOne = i === 0;
            const IconComponent = c.icon;

            return (
              <div
                key={c.title}
                className={`group relative overflow-hidden rounded-[26px] p-7 md:p-8 transition-all duration-300 ${
                  isCardOne ? "revops-cap-card-one" : "revops-cap-card-std"
                }`}
                style={{
                  background: isCardOne
                    ? "linear-gradient(135deg, #FFFFFF 0%, #F4FAFF 100%)"
                    : "rgba(255,255,255,0.96)",
                  border: isCardOne
                    ? "1.5px solid rgba(11,124,242,0.36)"
                    : "1px solid rgba(8,46,103,0.07)",
                  boxShadow: isCardOne
                    ? "0 14px 40px rgba(11,124,242,0.08), 0 2px 6px rgba(8,46,103,0.025)"
                    : "0 10px 32px rgba(8,46,103,0.045), 0 2px 6px rgba(8,46,103,0.02)",
                  minHeight: "205px",
                }}
              >
                {/* Large pale watermark number */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute top-3 right-6 select-none font-display font-black leading-none"
                  style={{
                    fontSize: "clamp(74px, 5.6vw, 92px)",
                    color: isCardOne
                      ? "rgba(11,124,242,0.12)"
                      : "rgba(11,124,242,0.08)",
                    letterSpacing: "-0.06em",
                    lineHeight: 0.88,
                  }}
                >
                  {numStr}
                </div>

                {/* Header Row of Card: Icon + Optional "DEPLOYED AT SCALE" badge right beside it */}
                <div className="relative z-10 flex items-center gap-3 mb-5">
                  <div
                    className="rounded-[16px] flex items-center justify-center shrink-0"
                    style={{
                      background: "#EDF7FF",
                      width: 52,
                      height: 52,
                    }}
                  >
                    <IconComponent
                      className="w-6 h-6"
                      style={{ color: "#0B7CF2" }}
                    />
                  </div>

                  {isCardOne && (
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-sans font-bold shrink-0"
                      style={{
                        background: "#E9FFF0",
                        color: "#169846",
                        fontSize: "10px",
                        letterSpacing: "0.06em",
                      }}
                    >
                      DEPLOYED AT SCALE
                    </span>
                  )}
                </div>

                {/* Card Title */}
                <h4
                  className="relative z-10 font-display font-bold mb-2.5"
                  style={{
                    fontSize: "clamp(19px, 1.4vw, 22.5px)",
                    lineHeight: 1.2,
                    letterSpacing: "-0.025em",
                    color: "#071E61",
                  }}
                >
                  {c.title}
                </h4>

                {/* Card Description */}
                <p
                  className="relative z-10"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "14.5px",
                    lineHeight: 1.5,
                    color: "#536B89",
                  }}
                >
                  {c.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom Architecture Closing Conclusion Strip */}
        <div
          className="mt-10 md:mt-12 mx-auto max-w-[1080px] flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 px-7 md:px-12 py-4.5 sm:py-5 rounded-[26px] sm:rounded-full"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(238,248,255,0.98) 100%)",
            border: "1.5px solid rgba(11,124,242,0.22)",
            boxShadow:
              "0 12px 34px rgba(8,46,103,0.05), 0 2px 6px rgba(11,124,242,0.03)",
          }}
        >
          {/* Conclusion Icon Container */}
          <div
            className="w-12 h-12 rounded-[16px] flex items-center justify-center shrink-0"
            style={{
              background: "#EDF7FF",
              border: "1px solid rgba(11,124,242,0.14)",
            }}
          >
            <Boxes className="w-6 h-6" style={{ color: "#0B7CF2" }} />
          </div>

          {/* Vertical separator on desktop */}
          <div
            aria-hidden="true"
            className="hidden sm:block w-px h-8 bg-blue-500/20"
          />

          {/* Architecture Statement */}
          <p
            className="text-center sm:text-left font-display font-bold"
            style={{
              fontSize: "clamp(16.5px, 1.35vw, 20px)",
              lineHeight: 1.35,
              color: "#071E61",
              letterSpacing: "-0.015em",
            }}
          >
            {config.archConclusionText
              .replace(config.archConclusionHighlight, "")
              .trim()}{" "}
            <span style={{ color: "#0B7CF2" }}>
              {config.archConclusionHighlight}
            </span>
          </p>
        </div>

        {/* Section CTA inside the section */}
        <SectionCta
          copy="Want this built for your system? Let's scope it."
          label="Scope My Build"
        />
      </div>
    </section>
  );
}
